import { alpha, Box, Drawer, type Theme } from '@mui/material';
import { useEffect, useState, type CSSProperties } from 'react';
import BetslipInfoMessage from '../../../atoms/BetslipInfoMessage';
import FullScreenLoader from '../../../atoms/FullScreenLoader';
import NeobetBetBuilderBetslipItem from '../molecules/NeobetBetBuilderBetslipItem';
import NeobetBetslipSelectionCard from '../molecules/NeobetBetslipSelectionCard';
import NeobetBetslipFooter from '../molecules/NeobetBetslipFooter';
import NeobetBetslipHeader from '../molecules/NeobetBetslipHeader';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useShouldShowBetslip from '../../../../hooks/useShouldShowBetslip';
import { useNeobetBetslipDrawer } from '../../../../hooks/components/useNeobetBetslipDrawer';
import { NEOBET_HEADER_HEIGHT } from '../../../../config/skinRegistry/configs';
import { NEOBET_OPEN_BETSLIP_EVENT } from '../constants';

const DESKTOP_PANEL_WIDTH = 400;

const styles = {
  desktopRail: (collapsed: boolean) => ({
    position: 'fixed',
    right: 0,
    width: DESKTOP_PANEL_WIDTH,
    borderLeft: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
    boxShadow: (theme: Theme) => `-4px 0 24px ${alpha(theme.palette.common.black, 0.16)}`,
    transition: 'top 0.25s ease, height 0.25s ease',
    ...(collapsed
      ? { top: 'auto', bottom: 0, height: 'auto' }
      : { top: 0, height: '100vh', pt: `${NEOBET_HEADER_HEIGHT}px` }),
  }),
  desktopPanel: (collapsed: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    ...(collapsed ? {} : { height: `calc(100vh - ${NEOBET_HEADER_HEIGHT}px)` }),
  }),
  drawer: (isMobile: boolean) => ({
    zIndex: (theme: Theme) => theme.zIndex.drawer,
    '& .MuiDrawer-paper': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '100dvh',
      bottom: isMobile ? 0 : 16,
      top: 'auto',
      borderRadius: 0,
      overflow: 'hidden',
      boxShadow: (theme: Theme) => `0 24px 48px ${alpha(theme.palette.common.black, 0.36)}`,
    },
  }),
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  betList: {
    px: 1.25,
    pt: 1.25,
    pb: 1.25,
    display: 'grid',
    gap: 1,
  },
  contentScrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  footerWrapper: {
    mt: 'auto',
  },
} as const;

const NeobetBetslipDrawer = () => {
  const controller = useNeobetBetslipDrawer();
  const { isMobile } = useBreakpoints();
  const showBetslip = useShouldShowBetslip();
  const [collapsed, setCollapsed] = useState(false);
  const {
    open,
    setOpen,
    closeBetslipDrawer,
    hasPendingBetslips,
    hasReofferedBetslips,
    bets,
    infoMessages,
    placeBet,
    selectedMenuItem,
    primaryStakeMode,
    selectedWallet,
    stakeErrors,
    handleStakeChange,
    removeSingleStakeError,
    updateSinglesStakeAmount,
  } = controller;

  const hasBets = bets.length > 0;
  const isMobileDrawerVisible = open && !hasReofferedBetslips;
  const isDesktopPanelVisible = !isMobile && hasBets && open && !hasReofferedBetslips;

  useEffect(() => {
    if (!isMobile && hasBets && !open) {
      setOpen(true);
    }
  }, [hasBets, isMobile, open, setOpen]);

  useEffect(() => {
    const handleOpenDrawer = () => {
      if (bets.length > 0) {
        setOpen(true);
      }
    };

    window.addEventListener(NEOBET_OPEN_BETSLIP_EVENT, handleOpenDrawer);

    return () => {
      window.removeEventListener(NEOBET_OPEN_BETSLIP_EVENT, handleOpenDrawer);
    };
  }, [setOpen, bets.length]);

  const isDesktopCollapsed = !isMobile && collapsed;
  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  if (!showBetslip) {
    return null;
  }

  const panelContent = (
    <form onSubmit={placeBet} style={styles.form as CSSProperties} data-testid='neobet-betslip-panel'>
      {Array.from(infoMessages).map((message) => (
        <BetslipInfoMessage key={message} message={message} />
      ))}

      {hasBets ? (
        <NeobetBetslipHeader
          controller={controller}
          isMobile={isMobile}
          collapsed={isDesktopCollapsed}
          onToggleCollapse={toggleCollapsed}
        />
      ) : null}

      {!isDesktopCollapsed && (
        <Box sx={styles.contentScrollArea} data-testid='neobet-betslip-scroll-region'>
          {hasBets ? (
            <Box sx={styles.betList}>
              {bets.map((bet) =>
                bet.betBuilder ? (
                  <NeobetBetBuilderBetslipItem
                    key={bet.outcomeId}
                    bet={bet}
                    selectedWallet={selectedWallet}
                    stakeErrorType={stakeErrors[bet.outcomeId]?.errorType}
                    handleStakeChange={handleStakeChange}
                    removeSingleStakeError={removeSingleStakeError}
                    updateSinglesStakeAmount={updateSinglesStakeAmount}
                    showStakeInput={primaryStakeMode !== 'single'}
                  />
                ) : (
                  <NeobetBetslipSelectionCard
                    key={bet.outcomeId}
                    bet={bet}
                    selectedMenuItem={selectedMenuItem}
                    primaryStakeMode={primaryStakeMode}
                    selectedWallet={selectedWallet}
                    stakeErrorType={stakeErrors[bet.outcomeId]?.errorType}
                    handleStakeChange={handleStakeChange}
                    removeSingleStakeError={removeSingleStakeError}
                    updateSinglesStakeAmount={updateSinglesStakeAmount}
                  />
                )
              )}
            </Box>
          ) : null}

          <Box sx={styles.footerWrapper}>
            <NeobetBetslipFooter controller={controller} />
          </Box>
        </Box>
      )}
    </form>
  );

  return (
    <>
      <FullScreenLoader open={hasPendingBetslips} />

      {isDesktopPanelVisible ? (
        <Box sx={styles.desktopRail(collapsed)} data-testid='neobet-betslip-desktop-rail'>
          <Box sx={styles.desktopPanel(collapsed)}>{panelContent}</Box>
        </Box>
      ) : null}

      {isMobile ? (
        <Drawer
          anchor='bottom'
          open={isMobileDrawerVisible}
          onClose={closeBetslipDrawer}
          sx={styles.drawer(isMobile)}
          ModalProps={{ keepMounted: true }}
        >
          {panelContent}
        </Drawer>
      ) : null}
    </>
  );
};

export default NeobetBetslipDrawer;
